import { useContext, useEffect, useRef, useState } from "react";
import { ReactComponent as Logo } from "../assets/images/logo/LOGO.svg";
import "./Navbar.css";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { useAuthUser } from "./dashboard/hooks/useAuthUser";
import HamburgerMenuButton from "./dashboard/components/menu/HamburgerMenuButton";
import { HamburgerMenuContext } from "./dashboard/store/HamburgerMenuContext";
import PublicMenuContent from "./dashboard/components/menu/PublicMenuContent";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import { PublicNavMainLinks, PublicNavMoreLinks } from "./PublicNavLinks";

const Navbar = () => {
  const { data: companyDetails } = useCompanyDetails();
  const {
    isAuthenticated,
    isAdmin,
    isEmployee,
    isBusiness,
    hasCSASubscription,
    isSocial,
    logout,
  } = useAuthUser();
  const { openHamburgerMenu } = useContext(HamburgerMenuContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktopProfileMenu, setIsDesktopProfileMenu] = useState(false);
  const [isDesktopMoreMenu, setIsDesktopMoreMenu] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const navRef = useRef<HTMLElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const lastScrollYRef = useRef(0);
  const location = window.location.pathname;

  const handleLogout = () => {
    setMenuOpen(false);
    setIsDesktopProfileMenu(false);
    setIsDesktopMoreMenu(false);
    logout();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
        setIsDesktopProfileMenu(false);
        setIsDesktopMoreMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    const SCROLL_DELTA_THRESHOLD = 8;
    const appContainer = document.querySelector(".App") as HTMLElement | null;
    const getCurrentScrollY = () => {
      const windowScrollY =
        window.scrollY || document.documentElement.scrollTop;
      const appScrollY = appContainer?.scrollTop ?? 0;
      return Math.max(windowScrollY, appScrollY);
    };

    const handleScroll = () => {
      const currentScrollY = getCurrentScrollY();
      const scrollDelta = currentScrollY - lastScrollYRef.current;
      const isSignificantScroll =
        Math.abs(scrollDelta) > SCROLL_DELTA_THRESHOLD;
      const isScrollingUp = scrollDelta < 0;
      const isScrollingDown = scrollDelta > 0;
      const navHeight = navRef.current?.offsetHeight ?? 0;
      const isNearTop = currentScrollY < 16;
      const hasScrolledPastNavbar = currentScrollY > navHeight;

      if (isNearTop) {
        setIsNavVisible(true);
      } else if (isScrollingUp) {
        setIsNavVisible(true);
      } else if (isScrollingDown && isSignificantScroll) {
        setIsNavVisible(!hasScrolledPastNavbar);
      }

      lastScrollYRef.current = currentScrollY;
    };

    lastScrollYRef.current = getCurrentScrollY();
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    appContainer?.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      appContainer?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav
      ref={navRef}
      className={`navbar-container ${isNavVisible ? "" : "navbar-hidden"}`}
    >
      <div className="navbar-logo-and-burger">
        <div className="nav-logo-container">
          <Link to="/" className="logo-container">
            <Logo className="logo" />
          </Link>
          <h4 className="nav-brand-name">
            Alnarp’s
            <br /> Agroecology Farm
          </h4>
        </div>
        <div className="nav-socials-container">
          {companyDetails?.instagramLink && (
            <a
              href={companyDetails.instagramLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faInstagram} />
            </a>
          )}

          {companyDetails?.facebookLink && (
            <a
              href={companyDetails.facebookLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faFacebook} />
            </a>
          )}
          {companyDetails?.linkedinLink && (
            <a
              href={companyDetails.linkedinLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
          )}
        </div>
        <HamburgerMenuButton
          onClick={() => openHamburgerMenu(<PublicMenuContent />)}
        />
      </div>
      <ul className="navbar-main-menu">
        <PublicNavMainLinks pathname={location} />
        <div className="nav-relative-btn">
          <button
            className="nav-btn nav-link-desktop"
            onClick={() => {
              setIsDesktopProfileMenu(false);
              setIsDesktopMoreMenu((prev) => !prev);
              setMenuOpen((prev) => !prev);
            }}
          >
            More...
          </button>

          {menuOpen && isDesktopMoreMenu && (
            <div
              className={`burger-dropdown ${isDesktopProfileMenu ? "desktop-profile-dropdown" : ""}`}
              ref={dropdownRef}
            >
              <ul id="custom-dropdown-menu">
                {isDesktopMoreMenu && (
                  <PublicNavMoreLinks
                    pathname={location}
                    onNavigate={() => {
                      setMenuOpen(false);
                      setIsDesktopMoreMenu(false);
                    }}
                  />
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="nav-relative-btn">
          <button
            className="nav-btn nav-link-desktop"
            onClick={() => {
              setIsDesktopMoreMenu(false);
              setIsDesktopProfileMenu((prev) => !prev);
              setMenuOpen((prev) => !prev);
            }}
          >
            Account
            <FontAwesomeIcon icon={faUser} />
          </button>

          {menuOpen && isDesktopProfileMenu && (
            <div
              className={`burger-dropdown burger-dropdown-profile ${isDesktopProfileMenu ? "desktop-profile-dropdown" : ""}`}
              ref={dropdownRef}
            >
              <ul id="custom-dropdown-menu">
                {isAuthenticated && isDesktopProfileMenu ? (
                  <>
                    <li>
                      <Link
                        className="nav-link-desktop"
                        to="/profile"
                        onClick={() => {
                          setMenuOpen(false);
                          setIsDesktopProfileMenu(false);
                        }}
                      >
                        Profile
                      </Link>
                    </li>
                    <li>
                      {(isAdmin ||
                        isBusiness ||
                        isEmployee ||
                        hasCSASubscription) && (
                        <Link
                          className="nav-link-desktop"
                          style={{ whiteSpace: "nowrap" }}
                          to="/dashboard"
                        >
                          {isSocial && hasCSASubscription
                            ? "My Subscription"
                            : isBusiness
                              ? "My Orders"
                              : "Dashboard"}
                        </Link>
                      )}
                    </li>
                    <li>
                      <button
                        className="nav-btn nav-link-desktop"
                        onClick={handleLogout}
                      >
                        Log out
                      </button>
                    </li>
                  </>
                ) : (
                  !isAuthenticated &&
                  isDesktopProfileMenu && (
                    <>
                      <li>
                        <Link to="/login" className="nav-link-desktop">
                          Log in
                        </Link>
                      </li>
                      <li>
                        <Link to="/signup" className="nav-link-desktop">
                          Sign up
                        </Link>
                      </li>
                    </>
                  )
                )}
              </ul>
            </div>
          )}
        </div>
      </ul>
    </nav>
  );
};

export default Navbar;
